import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Chart, registerables } from 'chart.js';
import 'chartjs-adapter-date-fns';
import { supabaseClient } from '../../services/supabase';
import { standardLegend, standardTitle } from '../../utils/chartFormatting';
import { ChartLayout, OverviewContent, RelevanceContent, EquityContent, MethodologyContent } from './ChartLayout';
import { KeyMetrics, KeyMetric } from './KeyMetrics';
import { downloadChartAsPNG, downloadDataAsCSV, formatFilename } from '../../utils/downloadUtils';

Chart.register(...registerables);

interface Props {
  location: string;
}

/** Row from neighborhood_opportunity (2024 release; snake_case from Postgres). */
export interface NeighborhoodOppRow {
  location_name: string;
  geography_type: string;
  year: number;
  score: number | null;
  category: string | null;
  region?: string | null;
  employment_rate?: number | null;
  regional_median_employment_rate?: number | null;
  share_over_200_poverty?: number | null;
  regional_median_share_over_200_poverty?: number | null;
  share_bachelors_plus?: number | null;
  regional_median_share_bachelors_plus?: number | null;
  median_home_value?: number | null;
  regional_median_median_home_value?: number | null;
  share_proficient_math?: number | null;
  regional_median_share_proficient_math?: number | null;
  share_proficient_reading?: number | null;
  regional_median_share_proficient_reading?: number | null;
  high_school_grad_rate?: number | null;
  regional_median_high_school_grad_rate?: number | null;
  students_not_in_poverty?: number | null;
  regional_median_students_not_in_poverty?: number | null;
  [key: string]: unknown;
}

const YEAR = 2024;

const COMPARE_METRICS: {
  locKey: keyof NeighborhoodOppRow;
  regKey: keyof NeighborhoodOppRow;
  label: string;
  scale: 'pct' | 'thousands';
}[] = [
  { locKey: 'share_over_200_poverty', regKey: 'regional_median_share_over_200_poverty', label: '>200% poverty', scale: 'pct' },
  { locKey: 'share_bachelors_plus', regKey: 'regional_median_share_bachelors_plus', label: "Bachelor's or higher", scale: 'pct' },
  { locKey: 'employment_rate', regKey: 'regional_median_employment_rate', label: 'Employment rate', scale: 'pct' },
  { locKey: 'median_home_value', regKey: 'regional_median_median_home_value', label: 'Median home value ($K)', scale: 'thousands' },
  { locKey: 'share_proficient_math', regKey: 'regional_median_share_proficient_math', label: 'Math proficiency', scale: 'pct' },
  { locKey: 'share_proficient_reading', regKey: 'regional_median_share_proficient_reading', label: 'Reading proficiency', scale: 'pct' },
  { locKey: 'high_school_grad_rate', regKey: 'regional_median_high_school_grad_rate', label: 'High school grad rate', scale: 'pct' },
  { locKey: 'students_not_in_poverty', regKey: 'regional_median_students_not_in_poverty', label: 'Students not in poverty', scale: 'pct' }
];

function toChartValue(scale: 'pct' | 'thousands', v: number | null | undefined): number | null {
  if (v == null || !Number.isFinite(Number(v))) return null;
  const n = Number(v);
  if (scale === 'pct') return n * 100;
  return n / 1000;
}

function formatTooltip(scale: 'pct' | 'thousands', raw: number | null): string {
  if (raw == null || !Number.isFinite(Number(raw))) return '—';
  if (scale === 'pct') return `${(Number(raw) * 100).toFixed(1)}%`;
  return `$${Number(raw).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
}

export const NeighborhoodOpportunityChart: React.FC<Props> = ({ location }) => {
  const compareCanvasRef = useRef<HTMLCanvasElement | null>(null);
  const compareChartRef = useRef<Chart | null>(null);
  const distCanvasRef = useRef<HTMLCanvasElement | null>(null);
  const distChartRef = useRef<Chart | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [placeRow, setPlaceRow] = useState<NeighborhoodOppRow | null>(null);
  const [stateRow, setStateRow] = useState<NeighborhoodOppRow | null>(null);
  const [hasData, setHasData] = useState<boolean | null>(null);
  const [distRows, setDistRows] = useState<NeighborhoodOppRow[]>([]);
  const [activeChart, setActiveChart] = useState<'compare' | 'distribution'>('compare');
  const [activeView, setActiveView] = useState<'overview' | 'relevance' | 'equity' | 'methodology'>('overview');

  useEffect(() => {
    const load = async () => {
      if (!location) {
        setLoading(false);
        setHasData(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);

        const { data: locData, error: locErr } = await supabaseClient
          .from('neighborhood_opportunity')
          .select('*')
          .eq('year', YEAR)
          .eq('geography_type', 'place')
          .ilike('location_name', location)
          .limit(5);

        if (locErr) throw locErr;

        const match = (locData || []).find(
          r => String(r.location_name || '').toLowerCase() === location.toLowerCase()
        ) || (locData || [])[0];

        const { data: caData, error: caErr } = await supabaseClient
          .from('neighborhood_opportunity')
          .select('*')
          .eq('year', YEAR)
          .eq('geography_type', 'state')
          .ilike('location_name', 'California')
          .maybeSingle();

        if (caErr) throw caErr;

        if (!match || match.score == null) {
          setHasData(false);
          setPlaceRow(null);
          setStateRow(null);
          setLoading(false);
          return;
        }

        setPlaceRow(match as NeighborhoodOppRow);
        setStateRow((caData as NeighborhoodOppRow) || null);
        setHasData(true);
        setLoading(false);
      } catch (e: unknown) {
        console.error('NeighborhoodOpportunityChart: error loading data', e);
        setError(e instanceof Error ? e.message : 'Failed to load neighborhood opportunity data.');
        setHasData(false);
        setLoading(false);
      }
    };

    load();
  }, [location]);

  useEffect(() => {
    const loadDistribution = async () => {
      try {
        const { data, error: distErr } = await supabaseClient
          .from('neighborhood_opportunity')
          .select('location_name, geography_type, score, year')
          .eq('year', YEAR)
          .eq('geography_type', 'place');

        if (distErr) {
          return;
        }

        setDistRows((data || []) as NeighborhoodOppRow[]);
      } catch (e) {
        // distribution load failed
      }
    };

    loadDistribution();
  }, []);

  const compareChartData = useMemo(() => {
    if (!placeRow) return null;
    const labels: string[] = [];
    const localVals: (number | null)[] = [];
    const regVals: (number | null)[] = [];
    const rawLocal: (number | null)[] = [];
    const rawReg: (number | null)[] = [];
    const scales: ('pct' | 'thousands')[] = [];
    for (const m of COMPARE_METRICS) {
      const lvRaw = placeRow[m.locKey] as number | null;
      const rvRaw = placeRow[m.regKey] as number | null;
      const lv = toChartValue(m.scale, lvRaw);
      const rv = toChartValue(m.scale, rvRaw);
      if (lv == null && rv == null) continue;
      labels.push(m.label);
      localVals.push(lv);
      regVals.push(rv);
      rawLocal.push(lvRaw != null && Number.isFinite(Number(lvRaw)) ? Number(lvRaw) : null);
      rawReg.push(rvRaw != null && Number.isFinite(Number(rvRaw)) ? Number(rvRaw) : null);
      scales.push(m.scale);
    }
    return { labels, localVals, regVals, rawLocal, rawReg, scales };
  }, [placeRow]);

  useEffect(() => {
    if (activeChart !== 'compare') {
      if (compareChartRef.current) {
        try {
          compareChartRef.current.destroy();
        } catch {
          /* ignore */
        }
        compareChartRef.current = null;
      }
      return;
    }
    if (!compareCanvasRef.current || !hasData || !placeRow || !compareChartData?.labels.length) return;

    const ctx = compareCanvasRef.current.getContext('2d');
    if (!ctx) return;

    if (compareChartRef.current) {
      try {
        compareChartRef.current.destroy();
      } catch {
        /* ignore */
      }
      compareChartRef.current = null;
    }

    const { labels, localVals, regVals, rawLocal, rawReg, scales } = compareChartData;

    compareChartRef.current = new Chart(ctx, {
      type: 'bar',
      data: {
        labels,
        datasets: [
          {
            label: location,
            data: localVals as number[],
            backgroundColor: '#3b82f6',
            borderColor: '#1d4ed8',
            borderWidth: 1
          },
          {
            label: 'Regional median (benchmark)',
            data: regVals as number[],
            backgroundColor: 'rgba(148, 163, 184, 0.85)',
            borderColor: '#64748b',
            borderWidth: 1
          }
        ]
      },
      options: {
        indexAxis: 'y',
        responsive: true,
        maintainAspectRatio: false,
        interaction: { mode: 'index', intersect: false },
        plugins: {
          legend: standardLegend,
          title: standardTitle(`Local vs regional benchmark (${YEAR})`),
          tooltip: {
            callbacks: {
              label: (ctx: any) => {
                const i = ctx.dataIndex;
                const scale = scales[i];
                const raw = ctx.datasetIndex === 0 ? rawLocal[i] : rawReg[i];
                return `${ctx.dataset.label ?? ''}: ${formatTooltip(scale, raw)}`;
              }
            }
          }
        },
        scales: {
          x: {
            title: {
              display: true,
              text: 'Percent (%) or median home value ($ thousands)'
            },
            beginAtZero: true
          },
          y: {
            title: { display: false }
          }
        }
      }
    });

    return () => {
      if (compareChartRef.current) {
        try {
          compareChartRef.current.destroy();
        } catch {
          /* ignore */
        }
        compareChartRef.current = null;
      }
    };
  }, [activeChart, hasData, placeRow, compareChartData, location]);

  useEffect(() => {
    if (activeChart !== 'distribution') {
      if (distChartRef.current) {
        try {
          distChartRef.current.destroy();
        } catch {
          /* ignore */
        }
        distChartRef.current = null;
      }
      return;
    }
    if (!distCanvasRef.current || !distRows.length) return;

    const ctx = distCanvasRef.current.getContext('2d');
    if (!ctx) return;

    if (distChartRef.current) {
      try {
        distChartRef.current.destroy();
      } catch {
        /* ignore */
      }
      distChartRef.current = null;
    }

    const points = distRows
      .filter(r => r.score != null && Number.isFinite(Number(r.score)))
      .map(r => ({
        x: Number(r.score),
        y: (Math.random() - 0.5) * 0.4,
        label: r.location_name
      }));

    const locPoint = points.find(p => p.label.toLowerCase() === location.toLowerCase());

    distChartRef.current = new Chart(ctx, {
      type: 'scatter',
      data: {
        datasets: [
          {
            label: 'All places',
            data: points,
            backgroundColor: 'rgba(148, 163, 184, 0.7)',
            pointRadius: 3
          },
          ...(locPoint
            ? [
                {
                  label: location,
                  data: [locPoint],
                  backgroundColor: '#3b82f6',
                  pointRadius: 6
                }
              ]
            : [])
        ]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: standardLegend,
          title: standardTitle(`Opportunity score distribution (${YEAR})`),
          tooltip: {
            callbacks: {
              label: (ctx: any) => {
                const label = ctx.raw?.label || '';
                const v = ctx.parsed?.x;
                if (v == null) return label;
                return `${label}: ${v.toFixed(1)}`;
              }
            }
          }
        },
        scales: {
          x: {
            title: { display: true, text: 'Opportunity score (0–9)' }
          },
          y: { display: false }
        }
      }
    });

    return () => {
      if (distChartRef.current) {
        try {
          distChartRef.current.destroy();
        } catch {
          /* ignore */
        }
        distChartRef.current = null;
      }
    };
  }, [activeChart, distRows, location]);

  const handleDownloadPNG = () => {
    const ref = activeChart === 'compare' ? compareCanvasRef : distCanvasRef;
    const slug = activeChart === 'compare' ? 'Neighborhood_Opp_vs_Regional' : 'Neighborhood_Opp_Distribution';
    downloadChartAsPNG(ref, formatFilename(location, slug));
  };

  const handleDownloadCSV = () => {
    if (placeRow) downloadDataAsCSV([placeRow], formatFilename(location, 'Neighborhood_Opportunity_2024'));
  };

  if (loading) {
    return (
      <div style={{ height: 360, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div>Loading neighborhood opportunity data for {location}…</div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ height: 360, padding: 24, textAlign: 'center' }}>
        <div style={{ color: '#dc2626', marginBottom: 8 }}>{error}</div>
        <div style={{ color: '#6b7280', fontSize: 14 }}>Location: {location || 'Not set'}</div>
      </div>
    );
  }

  if (!hasData || !placeRow) {
    return (
      <div style={{ height: 360, padding: 24, textAlign: 'center' }}>
        <div>No neighborhood opportunity data available for {location} ({YEAR} release).</div>
      </div>
    );
  }

  const locScore = placeRow.score != null && Number.isFinite(Number(placeRow.score)) ? Number(placeRow.score) : null;
  const locCategory =
    placeRow.category != null && String(placeRow.category).trim() !== '' ? String(placeRow.category) : null;
  const caScore =
    stateRow?.score != null && Number.isFinite(Number(stateRow.score)) ? Number(stateRow.score) : null;
  const regionLabel = placeRow.region ? String(placeRow.region) : null;

  const keyMetricsDisplay =
    locScore != null ? (
      <KeyMetrics>
        <KeyMetric value={locScore.toFixed(1)} label={`Opportunity score (${YEAR})`} color="#3b82f6" />
        {caScore != null && (
          <KeyMetric value={caScore.toFixed(1)} label={`California mean (places, ${YEAR})`} color="#10b981" />
        )}
        {locCategory && (
          <KeyMetric
            value={locCategory.charAt(0).toUpperCase() + locCategory.slice(1)}
            label="Opportunity category"
            color="#6366f1"
          />
        )}
        {regionLabel && <KeyMetric value={regionLabel} label="Region (benchmark)" color="#0d9488" />}
      </KeyMetrics>
    ) : null;

  const overviewContent = (
    <OverviewContent
      title="Overview"
      description={`The ${YEAR} California opportunity map combines tract-level socioeconomic and education indicators with regional benchmarks.`}
    >
      <p>
        Bars compare {location} to the <strong>regional median</strong> used in the official methodology (same region as
        the majority of the city’s census tracts). The opportunity score (0–9) summarizes how many indicator thresholds
        the area meets relative to its region.
      </p>
    </OverviewContent>
  );

  const relevanceContent = (
    <RelevanceContent
      title="Relevance"
      description="Opportunity designations guide housing tax credits and highlight where resources and infrastructure investments matter."
    >
      <div className="methodology-details-simple">
        <h5>Economic & educational context</h5>
        <p>
          Employment, education, and poverty-related fields show how local conditions compare to the regional reference
          used in the statewide maps—not a national average.
        </p>
      </div>
    </RelevanceContent>
  );

  const equityContent = (
    <EquityContent
      title="Equity"
      description="Opportunity maps surface how access to well-resourced neighborhoods varies across communities."
    >
      <div className="methodology-details-simple">
        <h5>Regional comparison</h5>
        <p>
          Gaps between local values and the regional median highlight where a community is above or below the typical
          threshold set for its region in the {YEAR} release.
        </p>
      </div>
    </EquityContent>
  );

  const methodologyContent = (
    <MethodologyContent
      title="Data Sources and Methodology"
      description={`This section uses only the ${YEAR} opportunity-map tract file (CTCAC-style scores and regional medians).`}
      dataSources={[
        "California State Treasurer / CTCAC opportunity map tract extract (2024)",
        'Census tract-to-place crosswalk (2020-based) for city rollups'
      ]}
      methodology={[
        'Earlier years (2018–2023) in the shared “opp20xx” workbooks use composite / z-score style indices that are not comparable to the 2024 Opportunity Score. They are intentionally excluded from this pipeline.',
        'Block-group rows are averaged to the census tract, then tracts are rolled up to places (mean for shares and rates; median for tract median home value). Regional median columns are attached from the modal region among tracts in each place.',
        'The “California mean” metric is the unweighted mean of place-level opportunity scores across the state for the same year.'
      ]}
    />
  );

  const chartContent =
    activeChart === 'compare' ? (
      <div style={{ height: 400, width: '100%', position: 'relative' }}>
        <canvas ref={compareCanvasRef} style={{ height: '100%', width: '100%' }} />
      </div>
    ) : (
      <div style={{ height: 400, width: '100%', position: 'relative' }}>
        <canvas ref={distCanvasRef} style={{ height: '100%', width: '100%' }} />
      </div>
    );

  return (
    <ChartLayout
      title="Neighborhood Opportunity (2024)"
      key={`neighborhood-opportunity-${location}`}
      description={`${location} • ${YEAR} release: local indicators vs regional medians`}
      chartType="bar"
      chartSelector={{
        value: activeChart,
        onChange: (value: string) => setActiveChart(value as 'compare' | 'distribution'),
        options: [
          { value: 'compare', label: 'Indicators vs regional median' },
          { value: 'distribution', label: 'Opportunity score distribution' }
        ]
      }}
      keyMetrics={keyMetricsDisplay}
      chartContent={chartContent}
      activeView={activeView}
      onViewChange={(view: string) => setActiveView(view as 'overview' | 'relevance' | 'equity' | 'methodology')}
      showRelevanceEquity={true}
      layout="side-by-side"
      onDownloadPNG={handleDownloadPNG}
      onDownloadCSV={handleDownloadCSV}
      pngFilename={formatFilename(location, 'Neighborhood_Opportunity_2024')}
      csvFilename={formatFilename(location, 'Neighborhood_Opportunity_2024')}
      enableComments={true}
      location={location}
      appendLocationToTitle={true}
      overviewContent={overviewContent}
      relevanceContent={relevanceContent}
      equityContent={equityContent}
      methodologyContent={methodologyContent}
    />
  );
};

export default NeighborhoodOpportunityChart;
